import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { FaCheck, FaChevronDown, FaChevronRight, FaCopy, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { BsFillTvFill, BsInfoSquareFill } from "react-icons/bs";
import { LuTrainTrack } from "react-icons/lu";
import { MdInput, MdOutlineOutput } from "react-icons/md";
import { IoMdTrain } from "react-icons/io";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraRequirementExtBulkEditModal from "../components/EraRequirementExtBulkEditModal";
import EraRequirementFormModal from "../components/EraRequirementFormModal";
import EraRequirementModal from "../components/EraRequirementModal";

const pageSizeOptions = [
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
  { value: "all", label: "All" }
];

export default function EraRequirementsExt({ apiBase = "/api/era-requirements-ext", title = "ERA Requirements_v3+", user }) {
  const tableContainerRef = useRef(null);
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDmiOnly, setShowDmiOnly] = useState(false);
  const [showCoveredOnly, setShowCoveredOnly] = useState(false);
  const [showDmiIn, setShowDmiIn] = useState(false);
  const [showDmiOut, setShowDmiOut] = useState(false);
  const [hideHeadings, setHideHeadings] = useState(false);
  const [chapterFilter, setChapterFilter] = useState("all");
  const [hideDeleted, setHideDeleted] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [onboardFilter, setOnboardFilter] = useState("all");
  const [tracksideFilter, setTracksideFilter] = useState("all");
  const [showGrouped] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortField, setSortField] = useState("req_number");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRequirements, setTotalRequirements] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("RQM");

  const loadRequirements = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sortField,
        sortDir
      });

      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (showDmiOnly) params.set("dmi", "true");
      if (showCoveredOnly) params.set("coverage", "covered");
      if (showDmiIn) params.set("dmiIn", "true");
      if (showDmiOut) params.set("dmiOut", "true");
      if (hideHeadings) params.set("hideHeadings", "true");
      if (chapterFilter !== "all") params.set("chapter", chapterFilter);
      if (hideDeleted) params.set("hideDeleted", "true");
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      if (onboardFilter !== "all") params.set("onboard", onboardFilter);
      if (tracksideFilter !== "all") params.set("trackside", tracksideFilter);

      const res = await fetchWithAuth(`${apiBase}?${params.toString()}`);

      if (!res.ok) {
        console.error("Failed to load ERA requirements");
        return;
      }

      const data = await res.json();
      setRequirements(data.rows ?? []);
      setTotalRequirements(data.total ?? 0);
      setCategoryOptions(data.categoryOptions ?? []);
      setChapterOptions(data.chapterOptions ?? []);
      setSelectedIds([]);
    } catch (err) {
      console.warn("loadRequirements aborted:", err.message);
    }
  }, [apiBase, page, pageSize, sortField, sortDir, debouncedSearch, showDmiOnly, showCoveredOnly, showDmiIn, showDmiOut, hideHeadings, chapterFilter, hideDeleted, categoryFilter, onboardFilter, tracksideFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [showDmiOnly, showCoveredOnly, showDmiIn, showDmiOut, hideHeadings, chapterFilter, hideDeleted, categoryFilter, onboardFilter, tracksideFilter, sortField, sortDir]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  useEffect(() => {
    if (!tableContainerRef.current) return;

    tableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    tableContainerRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [page, pageSize]);
  const deleteRequirement = async (id) => {
    if (!window.confirm("Delete this ERA requirement?")) return;

    const res = await fetchWithAuth(`${apiBase}/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    loadRequirements();
  };

  const openRequirement = (requirement) => {
    if (isSection(requirement)) {
      return;
    }

    setSelectedRequirement(requirement);
    setShowModal(true);
  };

  const normalizeCategory = (value) => String(value ?? "").trim() || "none";
  const sectionCategories = new Set(["section", "others_sec"]);
  const deletedCategories = new Set(["others", "others_sec"]);
  const isSectionCategory = (value) => sectionCategories.has(normalizeCategory(value));
  const isDeletedCategory = (value) => deletedCategories.has(normalizeCategory(value));
  const isSection = (requirement) => isSectionCategory(requirement.category);
  const isRequirement = (requirement) => !isSection(requirement);
  const isCovered = (requirement) => Number(requirement.covering_test_cases_count ?? 0) > 0;
  const hasExtFlags = true;

  const filteredRequirements = requirements.filter((requirement) => {
    const query = debouncedSearch.trim().toLowerCase();
    const matchesSearch = !query || (
      String(requirement.req_number ?? "").toLowerCase().includes(query) ||
      String(requirement.req_title ?? "").toLowerCase().includes(query)
    );

    if (!matchesSearch) return false;

    const requirementCategory = normalizeCategory(requirement.category);

    if (hideHeadings && isSectionCategory(requirementCategory)) {
      return false;
    }

    if (hideDeleted && ["others", "others_sec"].includes(requirementCategory)) {
      return false;
    }

    if (showDmiOnly && !Boolean(requirement.dmi)) {
      return false;
    }

    if (showCoveredOnly && Number(requirement.covering_test_cases_count ?? 0) === 0) {
      return false;
    }

    if (categoryFilter !== "all" && requirementCategory !== categoryFilter) {
      return false;
    }

    if (onboardFilter !== "all" && Boolean(requirement.onboard) !== (onboardFilter === "yes")) {
      return false;
    }

    if (tracksideFilter !== "all" && Boolean(requirement.trackside) !== (tracksideFilter === "yes")) {
      return false;
    }

    const isIn = Boolean(requirement.dmi_in_all);
    const isOut = Boolean(requirement.dmi_out_all);

    if (showDmiIn && showDmiOut) {
      return isIn && isOut;
    }

    if (showDmiIn) {
      return isIn;
    }

    if (showDmiOut) {
      return isOut;
    }

    return true;
  });

  const buildRequirementNumberSortKey = (value) => {
    const text = String(value ?? "").trim();
    const match = text.match(/^(A\.)?(\d+(?:\.\d+)*)(?:-(.*))?$/i);

    if (!match) {
      return {
        section: Number.MAX_SAFE_INTEGER,
        appendix: 0,
        mainParts: [],
        suffix: "",
        suffixRank: 4,
        suffixParts: [],
        text
      };
    }

    const mainParts = match[2].split(".");

    return {
      section: Number(mainParts[0]),
      appendix: match[1] ? 1 : 0,
      suffix: match[3] ?? "",
      suffixRank: getSuffixRank(match[3] ?? ""),
      mainParts,
      suffixParts: match[3] ? match[3].split(/[.-]/).filter(Boolean) : [],
      text
    };
  };

  const getSuffixRank = (suffix) => {
    const normalizedSuffix = String(suffix ?? "").trim().toLowerCase();

    if (!normalizedSuffix) return 0;
    if (/^[a-z]$/.test(normalizedSuffix)) return 1;
    if (/^\d+(?:-[a-z])?$/.test(normalizedSuffix)) return 2;
    if (normalizedSuffix.startsWith("table")) return 3;
    if (normalizedSuffix.startsWith("figure")) return 5;
    return 4;
  };

  const compareNumberPart = (leftPart, rightPart) => {
    const leftNumber = Number(leftPart);
    const rightNumber = Number(rightPart);
    const bothNumeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);

    if (bothNumeric) {
      if (leftNumber < rightNumber) return -1;
      if (leftNumber > rightNumber) return 1;
      return 0;
    }

    const leftNumeric = !Number.isNaN(leftNumber);
    const rightNumeric = !Number.isNaN(rightNumber);

    if (leftNumeric !== rightNumeric) {
      return leftNumeric ? -1 : 1;
    }

    return String(leftPart).localeCompare(String(rightPart), undefined, {
      sensitivity: "base",
      numeric: true
    });
  };

  const compareRequirementNumbers = (leftValue, rightValue) => {
    const leftKey = buildRequirementNumberSortKey(leftValue);
    const rightKey = buildRequirementNumberSortKey(rightValue);

    if (leftKey.section !== rightKey.section) {
      return leftKey.section - rightKey.section;
    }

    if (leftKey.appendix !== rightKey.appendix) {
      return leftKey.appendix - rightKey.appendix;
    }

    const maxMainLength = Math.max(leftKey.mainParts.length, rightKey.mainParts.length);

    for (let index = 0; index < maxMainLength; index += 1) {
      const leftPart = leftKey.mainParts[index];
      const rightPart = rightKey.mainParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const partCompare = compareNumberPart(leftPart, rightPart);
      if (partCompare !== 0) return partCompare;
    }

    if (!leftKey.suffixParts.length && rightKey.suffixParts.length) return -1;
    if (leftKey.suffixParts.length && !rightKey.suffixParts.length) return 1;

    if (leftKey.suffixRank !== rightKey.suffixRank) {
      return leftKey.suffixRank - rightKey.suffixRank;
    }

    const maxSuffixLength = Math.max(leftKey.suffixParts.length, rightKey.suffixParts.length);

    for (let index = 0; index < maxSuffixLength; index += 1) {
      const leftPart = leftKey.suffixParts[index];
      const rightPart = rightKey.suffixParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const partCompare = compareNumberPart(leftPart, rightPart);
      if (partCompare !== 0) return partCompare;
    }

    return leftKey.text.localeCompare(rightKey.text, undefined, { sensitivity: "base", numeric: true });
  };

  const compareRequirements = (leftRequirement, rightRequirement) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const leftIsSection = isSection(leftRequirement);
    const rightIsSection = isSection(rightRequirement);

    if (sortField === "req_number") {
      const numberCompare = compareRequirementNumbers(
        leftRequirement.req_number,
        rightRequirement.req_number
      );

      if (numberCompare !== 0) return numberCompare * direction;

      if (leftIsSection !== rightIsSection) {
        return leftIsSection ? -1 : 1;
      }
    }

    if (sortField === "req_title") {
      const titleCompare = String(leftRequirement.req_title ?? "").localeCompare(
        String(rightRequirement.req_title ?? ""),
        undefined,
        { sensitivity: "base" }
      );

      if (titleCompare !== 0) return titleCompare * direction;
    }

    if (sortField === "dmi_in_all") {
      const inCompare = Number(Boolean(leftRequirement.dmi_in_all)) - Number(Boolean(rightRequirement.dmi_in_all));
      if (inCompare !== 0) return inCompare * direction;
    }

    if (sortField === "dmi") {
      const dmiCompare = Number(Boolean(leftRequirement.dmi)) - Number(Boolean(rightRequirement.dmi));
      if (dmiCompare !== 0) return dmiCompare * direction;
    }

    if (sortField === "dmi_out_all") {
      const outCompare = Number(Boolean(leftRequirement.dmi_out_all)) - Number(Boolean(rightRequirement.dmi_out_all));
      if (outCompare !== 0) return outCompare * direction;
    }

    const numberCompare = compareRequirementNumbers(
      leftRequirement.req_number,
      rightRequirement.req_number
    );

    if (numberCompare !== 0) return numberCompare;

    if (leftIsSection !== rightIsSection) {
      return leftIsSection ? -1 : 1;
    }

    return String(leftRequirement.req_title ?? "").localeCompare(
      String(rightRequirement.req_title ?? ""),
      undefined,
      { sensitivity: "base" }
    );
  };

  const sortedRequirements = [...filteredRequirements].sort(compareRequirements);

  const groupedRequirements = Object.values(
    sortedRequirements.reduce((groups, requirement) => {
      const groupKey = requirement.req_number ?? "";
      const existingGroup = groups[groupKey];

      if (existingGroup) {
        existingGroup.children.push(requirement);
        existingGroup.hasDmi = existingGroup.hasDmi || Boolean(requirement.dmi);
        existingGroup.allDmiIn = existingGroup.allDmiIn && Boolean(requirement.dmi_in_all);
        existingGroup.allDmiOut = existingGroup.allDmiOut && Boolean(requirement.dmi_out_all);
        existingGroup.hasCoverage = existingGroup.hasCoverage || isCovered(requirement);
        existingGroup.hasOnboard = existingGroup.hasOnboard || Boolean(requirement.onboard);
        existingGroup.hasTrackside = existingGroup.hasTrackside || Boolean(requirement.trackside);
        return groups;
      }

      groups[groupKey] = {
        req_number: requirement.req_number,
        children: [requirement],
        category: normalizeCategory(requirement.category),
        hasDmi: Boolean(requirement.dmi),
        allDmiIn: Boolean(requirement.dmi_in_all),
        allDmiOut: Boolean(requirement.dmi_out_all),
        hasCoverage: isCovered(requirement),
        hasOnboard: Boolean(requirement.onboard),
        hasTrackside: Boolean(requirement.trackside)
      };

      return groups;
    }, {})
  ).sort((leftGroup, rightGroup) => {
    const leftRepresentative = {
      req_number: leftGroup.req_number,
      req_title: leftGroup.children[0]?.req_title ?? "",
      category: leftGroup.category,
      dmi: leftGroup.hasDmi,
      dmi_in_all: leftGroup.allDmiIn,
      dmi_out_all: leftGroup.allDmiOut,
      covering_test_cases_count: leftGroup.hasCoverage ? 1 : 0
    };
    const rightRepresentative = {
      req_number: rightGroup.req_number,
      req_title: rightGroup.children[0]?.req_title ?? "",
      category: rightGroup.category,
      dmi: rightGroup.hasDmi,
      dmi_in_all: rightGroup.allDmiIn,
      dmi_out_all: rightGroup.allDmiOut,
      covering_test_cases_count: rightGroup.hasCoverage ? 1 : 0
    };

    return compareRequirements(leftRepresentative, rightRepresentative);
  });
  const isGroupSection = (group) => isSectionCategory(group.category);
  const visibleSelectableRequirements = sortedRequirements.filter(isRequirement);
  const allVisibleSelected = visibleSelectableRequirements.length > 0 && visibleSelectableRequirements.every((requirement) => selectedIds.includes(requirement.id));
  const selectedCount = selectedIds.length;

  const totalVisibleItems = totalRequirements;
  const isAllRows = pageSize === "all";
  const pageCount = isAllRows ? 1 : Math.max(1, Math.ceil(totalRequirements / pageSize));
  const pageStart = totalRequirements > 0 ? (isAllRows ? 1 : ((page - 1) * pageSize) + 1) : 0;
  const pageEnd = isAllRows ? totalRequirements : Math.min(page * pageSize, totalRequirements);

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const toggleGroup = (reqNumber) => {
    setExpandedGroups((current) => ({
      ...current,
      [reqNumber]: !current[reqNumber]
    }));
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setPage(1);
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setPage(1);
    setSortField(field);
    setSortDir("asc");
  };

  const toggleRequirementSelection = (id) => {
    setSelectedIds((current) => (
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    ));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !visibleSelectableRequirements.some((requirement) => requirement.id === id)));
      return;
    }

    setSelectedIds((current) => {
      const visibleIds = visibleSelectableRequirements.map((requirement) => requirement.id);
      return [...new Set([...current, ...visibleIds])];
    });
  };

  const openBulkEdit = () => {
    if (!selectedCount) {
      alert("Select at least one ERA requirement");
      return;
    }

    setShowBulkEdit(true);
  };

  const showCopyError = (error, fallbackMessage) => {
    const numbers = Array.isArray(error?.numbers) && error.numbers.length
      ? `\n${error.numbers.join("\n")}`
      : "";

    alert(`${error?.error || fallbackMessage}${numbers}`);
  };

  const copyRequirementToSystem = async (requirement) => {
    const res = await fetchWithAuth(`${apiBase}/${requirement.id}/copy-to-system`, {
      method: "POST"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      showCopyError(error, "Copy failed");
      return;
    }

    alert("Copied to DMI System Requirements");
  };

  const bulkCopyRequirementsToSystem = async () => {
    if (!selectedCount) {
      alert("Select at least one ERA requirement");
      return;
    }

    const res = await fetchWithAuth(`${apiBase}/bulk-copy-to-system`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: selectedIds })
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      showCopyError(error, "Bulk copy failed");
      return;
    }

    const result = await res.json().catch(() => null);
    const created = result?.created ?? 0;
    const skipped = result?.skipped ?? 0;
    alert(`Copied ${created} requirement${created === 1 ? "" : "s"} to DMI System Requirements${skipped ? `\nSkipped existing: ${skipped}` : ""}`);
  };

  const renderCoverageIcon = () => (
    <FaCheck size={14} color="#198754" />
  );

  const renderIconFilterButton = (active, onClick, label, content) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
      style={{
        width: "2rem",
        height: "2rem",
        padding: 0,
        borderColor: active ? "#0d6efd" : undefined,
        boxShadow: active ? "inset 0 0 0 1px rgba(13, 110, 253, 0.35)" : undefined,
        backgroundColor: active ? "rgba(13, 110, 253, 0.22)" : undefined
      }}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {content}
    </button>
  );

  const renderFlagSlot = (content, label, active = true) => (
    <span
      className={`d-inline-flex align-items-center justify-content-center ${active ? "" : "text-muted opacity-25"}`}
      style={{ width: "1.35rem", height: "1.35rem" }}
      title={label}
      aria-label={label}
    >
      {content}
    </span>
  );
  const renderDefinitionIcon = () => (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-1 text-white fw-semibold"
      style={{ width: "14px", height: "14px", fontSize: "0.62rem", lineHeight: 1, backgroundColor: "#6f42c1" }}
      title="Definition"
      aria-label="Definition"
    >
      d
    </span>
  );

  const renderCategoryIcon = (category) => {
    const normalizedCategory = normalizeCategory(category);

    if (normalizedCategory === "informative") {
      return <BsInfoSquareFill size={14} color="#0dcaf0" />;
    }

    if (normalizedCategory === "definition") {
      return renderDefinitionIcon();
    }

    return null;
  };

  const renderRequirementFlags = (requirement) => {
    if (!isRequirement(requirement)) return null;

    return (
      <div className="d-inline-grid" style={{ gridTemplateColumns: "repeat(7, 1.35rem)", gap: "0.15rem" }}>
        {renderFlagSlot(<IoMdTrain size={16} color="#9a5a16" />, "Onboard", Boolean(requirement.onboard))}
        {renderFlagSlot(<LuTrainTrack size={16} color="#111111" />, "Trackside", Boolean(requirement.trackside))}
        {renderFlagSlot(<BsFillTvFill size={14} color="#123c69" />, "DMI", Boolean(requirement.dmi))}
        {renderFlagSlot(<MdInput size={17} color="#0f766e" />, "DMI IN", Boolean(requirement.dmi_in_all))}
        {renderFlagSlot(<MdOutlineOutput size={17} color="#7c3aed" />, "DMI OUT", Boolean(requirement.dmi_out_all))}
        {renderFlagSlot(renderCoverageIcon(), isCovered(requirement) ? "Covered" : "Not covered", isCovered(requirement))}
        {renderFlagSlot(renderCategoryIcon(requirement.category), normalizeCategory(requirement.category), Boolean(renderCategoryIcon(requirement.category)))}
      </div>
    );
  };

  const renderGroupFlags = (group) => {
    if (isGroupSection(group)) return null;

    return (
      <div className="d-inline-grid" style={{ gridTemplateColumns: "repeat(7, 1.35rem)", gap: "0.15rem" }}>
        {renderFlagSlot(<IoMdTrain size={16} color="#9a5a16" />, "Onboard", group.hasOnboard)}
        {renderFlagSlot(<LuTrainTrack size={16} color="#111111" />, "Trackside", group.hasTrackside)}
        {renderFlagSlot(<BsFillTvFill size={14} color="#123c69" />, "DMI", group.hasDmi)}
        {renderFlagSlot(<MdInput size={17} color="#0f766e" />, "DMI IN", group.allDmiIn)}
        {renderFlagSlot(<MdOutlineOutput size={17} color="#7c3aed" />, "DMI OUT", group.allDmiOut)}
        {renderFlagSlot(renderCoverageIcon(), group.hasCoverage ? "Covered" : "Not covered", group.hasCoverage)}
        {renderFlagSlot(renderCategoryIcon(group.category), group.category, Boolean(renderCategoryIcon(group.category)))}
      </div>
    );
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  const getSectionRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(13, 110, 253, 0.07)" : "rgba(13, 110, 253, 0.03)"
  });

  const actionColumnStyle = {
    ...stickyHeaderStyle,
    width: "152px"
  };

  const actionCellStyle = {
    whiteSpace: "nowrap"
  };

  const actionButtonClass = "btn btn-sm me-1";

  const getNumberClassName = (requirement) => {
    const classNames = [];

    if (isSection(requirement)) {
      classNames.push("fw-bold", "text-primary-emphasis");
    } else {
      classNames.push("fw-bold");
    }

    if (isDeletedCategory(requirement.category)) {
      classNames.push("opacity-50");
    }

    return classNames.join(" ");
  };

  const getTitleClassName = (requirement) => {
    const classNames = [];

    if (isSection(requirement)) {
      classNames.push("fw-semibold", "text-primary-emphasis");
    }

    if (isDeletedCategory(requirement.category)) {
      classNames.push("opacity-50");
    }

    return classNames.join(" ") || undefined;
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">{title}</h4>
      </div>
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
          <input
            type="text"
            className="form-control pe-5"
            placeholder="Search ERA requirements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
              }}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ width: "230px" }}
            value={chapterFilter}
            onChange={(e) => setChapterFilter(e.target.value)}
            aria-label="Filter by chapter"
            title="Chapter"
          >
            <option value="all">All chapters</option>
            {chapterOptions.map((chapter) => (
              <option key={chapter.value} value={chapter.value}>{chapter.label}</option>
            ))}
          </select>
          <div className="form-check form-switch m-0">
            <input
              id="era-req-ext-hide-headings"
              type="checkbox"
              className="form-check-input"
              checked={hideHeadings}
              onChange={(e) => setHideHeadings(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="era-req-ext-hide-headings">
              Hide headings
            </label>
          </div>
          <div className="form-check form-switch m-0">
            <input
              id="era-req-ext-hide-deleted"
              type="checkbox"
              className="form-check-input"
              checked={hideDeleted}
              onChange={(e) => setHideDeleted(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="era-req-ext-hide-deleted">
              Hide deleted
            </label>
          </div>
          {hasExtFlags && (
            <select
              className="form-select form-select-sm"
              style={{ width: "150px" }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
              title="Category"
            >
              <option value="all">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          )}
          {hasExtFlags && (
            <>
              {renderIconFilterButton(
                onboardFilter === "yes",
                () => setOnboardFilter((current) => current === "yes" ? "all" : "yes"),
                "Onboard",
                <IoMdTrain size={16} color="#9a5a16" />
              )}
              {renderIconFilterButton(
                tracksideFilter === "yes",
                () => setTracksideFilter((current) => current === "yes" ? "all" : "yes"),
                "Trackside",
                <LuTrainTrack size={16} color="#111111" />
              )}
            </>
          )}
          {renderIconFilterButton(
            showDmiOnly,
            () => setShowDmiOnly((current) => !current),
            "Show DMI requirements",
            <BsFillTvFill size={15} />
          )}
          {renderIconFilterButton(
            showDmiIn,
            () => setShowDmiIn((current) => !current),
            "DMI IN",
            <MdInput size={17} color="#0f766e" />
          )}
          {renderIconFilterButton(
            showDmiOut,
            () => setShowDmiOut((current) => !current),
            "DMI OUT",
            <MdOutlineOutput size={17} color="#7c3aed" />
          )}
          {renderIconFilterButton(
            showCoveredOnly,
            () => setShowCoveredOnly((current) => !current),
            "Covered",
            renderCoverageIcon()
          )}
          {canManage && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                onClick={() => {
                  setEditingRequirement(null);
                  setShowForm(true);
                }}
              >
                <FaPlus />
                Add requirement
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={openBulkEdit}
                disabled={!selectedCount}
              >
                Bulk edit{selectedCount ? ` (${selectedCount})` : ""}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={bulkCopyRequirementsToSystem}
                disabled={!selectedCount}
                title="Copy selected to DMI System Requirements"
              >
                Bulk copy{selectedCount ? ` (${selectedCount})` : ""}
              </button>
            </>
          )}
          <span className="badge text-bg-secondary">
            Total: {totalVisibleItems}
          </span>
        </div>
      </div>

      <div ref={tableContainerRef} style={{ maxHeight: "60vh", overflowY: "auto" }}>
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            {canManage && (
              <th style={{ ...stickyHeaderStyle, width: "44px" }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  aria-label="Select all visible ERA requirements"
                />
              </th>
            )}
            <th
              style={{ ...stickyHeaderStyle, width: "160px", cursor: "pointer" }}
              onClick={() => handleSort("req_number")}
            >
              <span className="d-inline-flex align-items-center">
                Number
                {renderSortIndicator("req_number")}
              </span>
            </th>
            <th
              style={{ ...stickyHeaderStyle, cursor: "pointer" }}
              onClick={() => handleSort("req_title")}
            >
              <span className="d-inline-flex align-items-center">
                Title
                {renderSortIndicator("req_title")}
              </span>
            </th>
            <th
              className="text-center"
              style={{ ...stickyHeaderStyle, width: "170px" }}
              title="Onboard, Trackside, DMI, DMI IN, DMI OUT, Coverage, Category"
            >
              Properties
            </th>
            <th style={actionColumnStyle}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {showGrouped ? groupedRequirements.map((group, groupIndex) => (
            <Fragment key={group.req_number}>
              {(() => {
                const groupRequirement = group.children[0];
                const singleRequirementGroup = group.children.length === 1 && isRequirement(groupRequirement);

                return (
              <tr style={isGroupSection(group) ? getSectionRowStyle(groupIndex) : getRowStyle(groupIndex)}>
                {canManage && (
                  <td>
                    {singleRequirementGroup && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(groupRequirement.id)}
                        onChange={() => toggleRequirementSelection(groupRequirement.id)}
                        aria-label={`Select ERA requirement ${groupRequirement.req_number}`}
                      />
                    )}
                  </td>
                )}
                <td className={getNumberClassName(group)}>
                  {group.children.length > 1 ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center me-2"
                      onClick={() => toggleGroup(group.req_number)}
                      aria-label={expandedGroups[group.req_number] ? "Collapse group" : "Expand group"}
                    >
                      {expandedGroups[group.req_number] ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )}
                    </button>
                  ) : (
                    <span className="d-inline-block me-2" style={{ width: "14px" }} />
                  )}
                  {singleRequirementGroup ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 fw-bold text-decoration-none text-start"
                      onClick={() => openRequirement(groupRequirement)}
                    >
                      {renderHighlightedText(group.req_number)}
                    </button>
                  ) : (
                    <span>{renderHighlightedText(group.req_number)}</span>
                  )}
                </td>
                <td className={isGroupSection(group) ? getTitleClassName(group) : `text-muted ${isDeletedCategory(group.category) ? "opacity-50" : ""}`.trim()}>
                  {isGroupSection(group)
                    ? renderHighlightedText(group.children[0]?.req_title)
                    : singleRequirementGroup
                      ? renderHighlightedText(groupRequirement?.req_title)
                      : `${group.children.length} item${group.children.length === 1 ? "" : "s"}`}
                </td>
                <td className="text-center">
                  {renderGroupFlags(group)}
                </td>
                <td style={actionCellStyle}>
                  {canManage && singleRequirementGroup && (
                    <>
                      <button
                        className={`${actionButtonClass} btn-outline-primary`}
                        onClick={() => {
                          setEditingRequirement(groupRequirement);
                          setShowForm(true);
                        }}
                      >
                        <FaEdit />
                      </button>

                      <button
                        className={`${actionButtonClass} btn-outline-secondary`}
                        onClick={() => copyRequirementToSystem(groupRequirement)}
                        title="Copy to DMI System Requirements"
                        aria-label="Copy to DMI System Requirements"
                      >
                        <FaCopy />
                      </button>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteRequirement(groupRequirement.id)}
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </td>
              </tr>
                );
              })()}
              {group.children.length > 1 && expandedGroups[group.req_number] && group.children.map((requirement, childIndex) => (
                <tr
                  key={requirement.id}
                  style={{
                    "--bs-table-bg": childIndex % 2 === 0
                      ? "rgba(25, 135, 84, 0.10)"
                      : "rgba(25, 135, 84, 0.03)"
                  }}
                >
                  {canManage && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(requirement.id)}
                        onChange={() => toggleRequirementSelection(requirement.id)}
                        aria-label={`Select ERA requirement ${requirement.req_number}`}
                      />
                    </td>
                  )}
                  <td className={`ps-4 text-muted ${isDeletedCategory(requirement.category) ? "opacity-50" : ""}`}>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none text-start"
                      onClick={() => openRequirement(requirement)}
                    >
                      {renderHighlightedText(requirement.req_number)}
                    </button>
                  </td>
                  <td className={getTitleClassName(requirement)}>{renderHighlightedText(requirement.req_title)}</td>
                  <td className="text-center">
                    {renderRequirementFlags(requirement)}
                  </td>
                  <td style={actionCellStyle}>
                    {canManage && isRequirement(requirement) && (
                      <>
                        <button
                          className={`${actionButtonClass} btn-outline-primary`}
                          onClick={() => {
                            setEditingRequirement(requirement);
                            setShowForm(true);
                          }}
                        >
                          <FaEdit />
                        </button>

                        <button
                          className={`${actionButtonClass} btn-outline-secondary`}
                          onClick={() => copyRequirementToSystem(requirement)}
                          title="Copy to DMI System Requirements"
                          aria-label="Copy to DMI System Requirements"
                        >
                          <FaCopy />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteRequirement(requirement.id)}
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          )) : sortedRequirements.map((requirement, index) => (
            <tr
              key={requirement.id}
              style={isSection(requirement) ? getSectionRowStyle(index) : getRowStyle(index)}
            >
              {canManage && (
                <td>
                  {isRequirement(requirement) && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(requirement.id)}
                      onChange={() => toggleRequirementSelection(requirement.id)}
                      aria-label={`Select ERA requirement ${requirement.req_number}`}
                    />
                  )}
                </td>
              )}
              <td className={getNumberClassName(requirement)}>
                {isRequirement(requirement) ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-bold text-decoration-none text-start"
                    onClick={() => openRequirement(requirement)}
                  >
                    {renderHighlightedText(requirement.req_number)}
                  </button>
                ) : (
                  renderHighlightedText(requirement.req_number)
                )}
              </td>
              <td className={getTitleClassName(requirement)}>
                {renderHighlightedText(requirement.req_title)}
              </td>
              <td className="text-center">
                {renderRequirementFlags(requirement)}
              </td>
              <td style={actionCellStyle}>
                {canManage && isRequirement(requirement) && (
                  <>
                    <button
                      className={`${actionButtonClass} btn-outline-primary`}
                      onClick={() => {
                        setEditingRequirement(requirement);
                        setShowForm(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className={`${actionButtonClass} btn-outline-secondary`}
                      onClick={() => copyRequirementToSystem(requirement)}
                      title="Copy to DMI System Requirements"
                      aria-label="Copy to DMI System Requirements"
                    >
                      <FaCopy />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteRequirement(requirement.id)}
                    >
                      <FaTrash />
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
        <div className="small text-muted">
          Showing {pageStart}-{pageEnd} of {totalRequirements}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label className="small text-muted" htmlFor="era-req-ext-page-size">
            Rows
          </label>
          <select
            id="era-req-ext-page-size"
            className="form-select form-select-sm"
            style={{ width: "90px" }}
            value={pageSize}
            onChange={(event) => {
              setPage(1);
              setPageSize(event.target.value === "all" ? "all" : Number(event.target.value));
            }}
          >
            {pageSizeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <div className="btn-group btn-group-sm" role="group" aria-label="ERA requirements pagination">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage(1)}
              disabled={page <= 1}
            >
              First
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button type="button" className="btn btn-outline-secondary" disabled>
              {page} / {pageCount}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
            >
              Next
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage(pageCount)}
              disabled={page >= pageCount}
            >
              Last
            </button>
          </div>
        </div>
      </div>
      <EraRequirementFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadRequirements}
        requirement={editingRequirement}
        apiBase={apiBase}
        showExtendedFields
      />

      <EraRequirementExtBulkEditModal
        show={showBulkEdit}
        onClose={() => setShowBulkEdit(false)}
        onSaved={loadRequirements}
        selectedCount={selectedCount}
        selectedIds={selectedIds}
        apiBase={apiBase}
      />

      <EraRequirementModal
        show={showModal}
        onClose={() => setShowModal(false)}
        requirement={selectedRequirement}
        apiBase={apiBase}
        title={title}
      />
    </div>
  );
}







